import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar from "../../../components/Sidebar";
import CustomInput from "../../../components/CustomInput";
import CustomDropdown from "../../../components/CustomDropdown";
import CustomButton from "../../../components/CustomButton";
import TransactionPasswordDialog from "../../../components/TransactionPasswordDialog";
import { getHomePageInfos, getTradingAccountsTrx } from "../../../utils/apiClient";
import { createDepositDemoOperation } from "../../../controller/accountsDemo/createDepositDemoController";
import AppConstants from "../../../utils/appConstants";
import { AccountType } from "../../../utils/enums";
import {
  getPermissionControllers,
  hasCustomerPermission,
  isAccountApprovalLocked,
} from "../../../utils/permissionHelper";
import PermissionCustomerModelController from "../../../controller/model/PermissionCustomerModelController";
import { useToast } from "../../../contexts/ToastContext";
import "../../css/cachIn.css";

const formatWalletLabel = (item) => {
  const tradingId =
    item.account?.account_information?.TRADING_ID ||
    item.account?.accountInformation?.tradingId ||
    item.TRADING_ID ||
    item.trading_id ||
    "-";
  const balance =
    item.AMOUNT ||
    item.CURRENT_BALENCE ||
    item.CURRENT_BALANCE ||
    item.BALANCE ||
    0;
  const currencySymbol =
    item.currencies?.CURRENCY_SYMBOL ||
    item.currency?.currencySymbol ||
    item.CURRENCY_SYMBOL ||
    "";
  const currencyCode =
    item.currencies?.CURRENCY_CODE ||
    item.currency?.currencyCode ||
    item.CURRENCY_CODE ||
    "";
  const numericBalance =
    typeof balance === "string"
      ? parseFloat(balance.replace(/,/g, ""))
      : parseFloat(balance);
  const formattedBalance = Number.isNaN(numericBalance) ? 0 : numericBalance;
  return `${tradingId} (${formattedBalance.toFixed(2)} ${currencySymbol || currencyCode})`;
};

export default function DemoDeposit() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { success: showSuccess, error: showError, warning: showWarning } = useToast();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [errors, setErrors] = useState({});

  const [amount, setAmount] = useState("");
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [walletOptions, setWalletOptions] = useState([]);

  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordDialogData, setPasswordDialogData] = useState(null);

  const permissions = useMemo(() => getPermissionControllers(user), [user]);
  const { customerPerm, customerDevicePerm } = permissions;
  const approvalLocked = useMemo(() => isAccountApprovalLocked(user), [user]);

  const canDepositDemo =
    !approvalLocked &&
    hasCustomerPermission(
      customerPerm,
      customerDevicePerm,
      PermissionCustomerModelController.PER__CAN_DEPOSIT_DEMO
    );

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setErrors({});

        let currentUser = null;
        try {
          const storedUserData = sessionStorage.getItem("userData");
          const storedTimestamp = sessionStorage.getItem("userDataTimestamp");
          if (storedUserData && storedTimestamp) {
            const age = Date.now() - parseInt(storedTimestamp, 10);
            if (age < 5 * 60 * 1000) {
              currentUser = JSON.parse(storedUserData);
              setUser(currentUser);
            }
          }
        } catch (_) {}

        try {
          const userResponse = await getHomePageInfos();
          const userData = userResponse?.decryptedData || userResponse?.data;
          if (userData?.CODE === AppConstants.responseOk && userData.DATA) {
            currentUser = userData.DATA;
            setUser(currentUser);
            sessionStorage.setItem("userData", JSON.stringify(currentUser));
            sessionStorage.setItem("userDataTimestamp", Date.now().toString());
          }
        } catch (_) {}

        const walletsResponse = await getTradingAccountsTrx([AccountType.DEMO], 0);
        const walletsData = walletsResponse?.decryptedData || walletsResponse?.data;

        if (walletsData?.CODE === AppConstants.responseOk && walletsData.DATA) {
          const list = Array.isArray(walletsData.DATA) ? walletsData.DATA : [];
          const options = [];

          list.forEach((item) => {
            const currencyType =
              item.currencies?.TYPE ??
              item.currency?.type ??
              item.CURRENCY_TYPE ??
              item.currencyType ??
              null;
            const accountType =
              item.account?.TYPE ??
              item.account?.type ??
              item.ACCOUNT_TYPE ??
              item.accountType ??
              null;
            const walletId = item.ID ?? item.id ?? item.WALLET_ID;
            const isTradingAccount = currencyType === 3 || (currencyType === null && walletId);
            const isDemoAccount =
              accountType === AccountType.DEMO || accountType === null;

            if (!walletId || !isTradingAccount || !isDemoAccount) return;

            options.push({
              value: walletId,
              label: formatWalletLabel(item),
              data: item,
            });
          });

          setWalletOptions(options);
          if (options.length === 1) {
            setSelectedWallet(options[0].value);
          }
        } else {
          setWalletOptions([]);
        }
      } catch (err) {
        showError(
          t("error_occurred", { ns: "mobile", defaultValue: "Error Occurred" }),
          err.message || t("try_again", { ns: "mobile", defaultValue: "Please try again." })
        );
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [showError, t]);

  const selectedWalletData = useMemo(
    () => walletOptions.find((opt) => opt.value === selectedWallet)?.data || null,
    [walletOptions, selectedWallet]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (processing) return;

    const nextErrors = {};
    const amountValue = parseFloat(String(amount || "").replace(/,/g, ""));
    if (!amount || Number.isNaN(amountValue) || amountValue <= 0) {
      nextErrors.amount = t("please_enter_valid_amount", {
        ns: "mobile",
        defaultValue: "Please enter a valid amount.",
      });
    }
    if (!selectedWallet) {
      nextErrors.wallet = t("select_an_account", {
        ns: "mobile",
        defaultValue: "Please select an account.",
      });
    }
    if (!canDepositDemo) {
      showError(
        t("error_occurred", { ns: "mobile", defaultValue: "Error Occurred" }),
        t("you_dont_have_access_to_do_this_operation", {
          ns: "mobile",
          defaultValue: "You don't have access to do this operation.",
        })
      );
      return;
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    try {
      setProcessing(true);
      setErrors({});

      const res = await createDepositDemoOperation(amountValue, selectedWallet);
      if (res.status && res.status >= 400) {
        const d = res.decryptedData || res.data || {};
        throw new Error(d.MESSAGE || d.message || d.ERROR || `Server error (${res.status})`);
      }

      const d = res.decryptedData || res.data || {};
      const code = Number(d.CODE ?? d.code ?? 0);
      if (code && code !== 200 && code !== AppConstants.responseOk) {
        throw new Error(
          d.MESSAGE || d.message || d.ERROR || "Failed to create demo deposit operation."
        );
      }

      const txId =
        d.TRANSACTION_ID || d.transactionId || d.REQUEST_TOKEN || d.requestToken || null;
      if (!txId) {
        throw new Error("TRANSACTION_ID is missing in server response.");
      }

      const currencySymbol =
        selectedWalletData?.currencies?.CURRENCY_SYMBOL ||
        selectedWalletData?.currency?.currencySymbol ||
        selectedWalletData?.CURRENCY_SYMBOL ||
        "";

      setPasswordDialogData({
        requestToken: txId,
        firstMessage: t("you_will_deposit", {
          ns: "mobile",
          defaultValue: "You will deposit",
        }) + ` ${amountValue} ${currencySymbol}.`,
        secondMessage: "",
      });
      setShowPasswordDialog(true);
    } catch (err) {
      showError(
        t("error_occurred", { ns: "mobile", defaultValue: "Error Occurred" }),
        err.message || t("try_again", { ns: "mobile", defaultValue: "Please try again." })
      );
    } finally {
      setProcessing(false);
    }
  };

  const handlePasswordDialogClose = async (result) => {
    setShowPasswordDialog(false);
    setPasswordDialogData(null);

    if (result === "ok" || result === "need_admin") {
      showSuccess(
        result === "ok"
          ? t("transaction_done", { ns: "mobile", defaultValue: "Transaction completed" })
          : t("transaction_pending", { ns: "mobile", defaultValue: "Transaction pending" }),
        result === "ok"
          ? t("deposit_to_demo_description", {
              ns: "mobile",
              defaultValue: "Add funds to your demo trading account",
            })
          : t("transaction_pending_admin", {
              ns: "mobile",
              defaultValue: "Transaction is pending admin approval.",
            })
      );
      setAmount("");
      navigate("/pages/accounts/demo");
    } else if (result === "canceled") {
      showWarning(
        t("transaction_canceled", { ns: "mobile", defaultValue: "Transaction canceled" }),
        t("deposit_demo", { ns: "mobile", defaultValue: "Demo Deposit" })
      );
    } else if (result === "failed") {
      showError(
        t("transaction_failed", { ns: "mobile", defaultValue: "Transaction failed" }),
        t("try_again", { ns: "mobile", defaultValue: "Please try again." })
      );
    }
  };

  return (
    <div className="page-layout">
      <Sidebar />
      <div className="main-content cash-in-page">
        <div className="cash-in-container">
          <div className="cash-in-header">
            <button
              type="button"
              className="cash-in-back-btn"
              onClick={() => navigate("/pages/accounts/demo")}
              aria-label={t("back", { ns: "mobile", defaultValue: "Back" })}
            >
              <i className="fa-solid fa-arrow-left" />
            </button>
            <h1>{t("deposit_demo", { ns: "mobile", defaultValue: "Deposit Demo" })}</h1>
            <p>
              {t("deposit_to_demo_description", {
                ns: "mobile",
                defaultValue: "Add funds to your demo trading account",
              })}
            </p>
          </div>

          {loading ? (
            <div className="cash-in-loading">{t("wait_a_moment", { ns: "mobile", defaultValue: "Please wait..." })}</div>
          ) : !canDepositDemo ? (
            <div className="cash-in-error-message">
              {t("you_dont_have_access_to_do_this_operation", {
                ns: "mobile",
                defaultValue: "You don't have access to do this operation.",
              })}
            </div>
          ) : (
            <form className="cash-in-form" onSubmit={handleSubmit}>
              <div className="cash-in-form-group">
                <label className="cash-in-form-label">
                  {t("amount_to_deposit", { ns: "mobile", defaultValue: "Amount to deposit" })}
                </label>
                <CustomInput
                  type="text"
                  placeholder={t("enter_the_amount_to_deposit", {
                    ns: "mobile",
                    defaultValue: "Enter the amount to deposit",
                  })}
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    if (errors.amount) setErrors((prev) => ({ ...prev, amount: null }));
                  }}
                  className={errors.amount ? "cash-in-input-error" : ""}
                  disabled={processing}
                />
                {errors.amount && <div className="cash-in-error-text">{errors.amount}</div>}
              </div>

              <div className="cash-in-form-group">
                <label className="cash-in-form-label">
                  {t("to_account", { ns: "mobile", defaultValue: "To account" })}
                </label>
                <CustomDropdown
                  options={walletOptions}
                  value={selectedWallet}
                  onChange={(val) => {
                    setSelectedWallet(val);
                    if (errors.wallet) setErrors((prev) => ({ ...prev, wallet: null }));
                  }}
                  placeholder={t("select_an_account", {
                    ns: "mobile",
                    defaultValue: "Select an account",
                  })}
                  disabled={processing || walletOptions.length === 0}
                  searchable
                />
                {errors.wallet && <div className="cash-in-error-text">{errors.wallet}</div>}
              </div>

              <CustomButton
                type="submit"
                className="cash-in-submit-btn"
                disabled={processing || walletOptions.length === 0}
              >
                {processing
                  ? t("wait_a_moment", { ns: "mobile", defaultValue: "Please wait..." })
                  : t("request", { ns: "mobile", defaultValue: "Request" })}
              </CustomButton>
            </form>
          )}
        </div>
      </div>

      <TransactionPasswordDialog
        open={showPasswordDialog && !!passwordDialogData?.requestToken}
        onClose={handlePasswordDialogClose}
        mode="demo_deposit"
        requestToken={passwordDialogData?.requestToken || ""}
        firstMessage={passwordDialogData?.firstMessage || ""}
        secondMessage={passwordDialogData?.secondMessage || ""}
      />
    </div>
  );
}
